import React from 'react';
import { useStyleThemeColors, fieldStyles, attributeInfo } from '../StyleEditor';
import { DEFAULT_LABELS, resolveLabels, formatNumber } from '../../lib/layer-style';

/**
 * Labels tab: edits the `labels` block of the draft style. MapDataLayers draws one
 * symbol sub-layer per labelled layer from it (lib/layer-style.js labelLayerSpec).
 */
export default function LabelsTab({ layer, style, set }) {
  const C = useStyleThemeColors();
  const { sel, lbl } = fieldStyles(C);
  const attrs = React.useMemo(() => attributeInfo(layer.features), [layer.features]);
  if (layer.kind !== 'vector') {
    return <div style={{ fontSize: 11, color: C.text3 }}>Labels apply to vector layers with attributes. Raster and vector-tile layers have no label field.</div>;
  }
  const labels = resolveLabels(style) || { ...DEFAULT_LABELS, ...(style.labels || {}) };
  const setLabels = (patch) => set({ labels: { ...labels, ...patch } });
  const colorBox = { width: '100%', height: 28, padding: 0, border: `1px solid ${C.border}`, borderRadius: 6, background: C.bg4, cursor: 'pointer' };
  const sampleRaw = layer.features.find((f) => f.properties?.[labels.field] != null)?.properties?.[labels.field];
  const numericField = attrs.find((a) => a.key === labels.field)?.isNumeric;
  const showLine = layer.geomType === 'line' || !layer.geomType;

  return (
    <>
      <div>
        <label style={lbl} htmlFor="ugis-label-field">Label field</label>
        <select id="ugis-label-field" value={labels.field} style={sel} onChange={(e) => setLabels({ field: e.target.value })}>
          <option value="">No labels</option>
          {attrs.map((a) => <option key={a.key} value={a.key}>{a.key}{a.isNumeric ? ' (#)' : ''}</option>)}
        </select>
      </div>
      {labels.field && (
        <>
          <div>
            <label style={lbl} htmlFor="ugis-label-size">Font size — {labels.size}px</label>
            <input id="ugis-label-size" type="range" min={7} max={24} step={1} value={labels.size}
              onChange={(e) => setLabels({ size: Number(e.target.value) })} style={{ width: '100%', accentColor: C.cyan }} />
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <div style={{ flex: 1 }}>
              <label style={lbl} htmlFor="ugis-label-color">Colour</label>
              <input id="ugis-label-color" type="color" value={labels.color} onChange={(e) => setLabels({ color: e.target.value })} style={colorBox} />
            </div>
            <div style={{ flex: 1 }}>
              <label style={lbl} htmlFor="ugis-label-halo">Halo</label>
              <input id="ugis-label-halo" type="color" value={labels.haloColor} onChange={(e) => setLabels({ haloColor: e.target.value })} style={colorBox} />
            </div>
          </div>
          <div>
            <label style={lbl} htmlFor="ugis-label-halo-width">Halo width — {labels.haloWidth}px</label>
            <input id="ugis-label-halo-width" type="range" min={0} max={4} step={0.5} value={labels.haloWidth}
              onChange={(e) => setLabels({ haloWidth: Number(e.target.value) })} style={{ width: '100%', accentColor: C.cyan }} />
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <div style={{ flex: 1 }}>
              <label style={lbl} htmlFor="ugis-label-placement">Placement</label>
              <select id="ugis-label-placement" value={labels.placement} style={sel} onChange={(e) => setLabels({ placement: e.target.value })}>
                <option value="point">Point</option>
                {showLine && <option value="line">Along line</option>}
              </select>
            </div>
            <div style={{ width: 80 }}>
              <label style={lbl} htmlFor="ugis-label-minzoom">Min zoom</label>
              <input id="ugis-label-minzoom" type="number" min={0} max={22} step={1} value={labels.minZoom}
                onChange={(e) => setLabels({ minZoom: Math.max(0, Math.min(22, Number(e.target.value) || 0)) })}
                style={{ ...sel, boxSizing: 'border-box' }} />
            </div>
          </div>
          <label style={{ fontSize: 11, color: C.text1, display: 'flex', alignItems: 'center', gap: 6 }}>
            <input type="checkbox" checked={!labels.allowOverlap} onChange={(e) => setLabels({ allowOverlap: !e.target.checked })} />
            Avoid label collisions
          </label>
          <div style={{ display: 'flex', gap: 8 }}>
            <div style={{ width: 90 }}>
              <label style={lbl} htmlFor="ugis-label-decimals">Decimals</label>
              <input id="ugis-label-decimals" type="number" min={0} max={12} step={1} value={labels.decimals ?? ''} placeholder="as is"
                disabled={!numericField}
                onChange={(e) => setLabels({ decimals: e.target.value === '' ? null : Math.max(0, Math.min(12, parseInt(e.target.value, 10) || 0)) })}
                style={{ ...sel, boxSizing: 'border-box' }} />
            </div>
            <div style={{ flex: 1 }}>
              <label style={lbl} htmlFor="ugis-label-suffix">Units suffix</label>
              <input id="ugis-label-suffix" type="text" value={labels.suffix} placeholder="e.g. Ωm" disabled={!numericField}
                onChange={(e) => setLabels({ suffix: e.target.value })} style={{ ...sel, boxSizing: 'border-box' }} />
            </div>
          </div>
          {sampleRaw !== undefined && (
            <div style={{ fontSize: 10, color: C.text2 }} data-testid="label-sample">
              Sample: <span style={{ color: C.text0 }}>{numericField ? formatNumber(sampleRaw, labels) : String(sampleRaw)}</span>
            </div>
          )}
        </>
      )}
    </>
  );
}
